// LandCalibration — Brian's directive #9 (2026-05-07).
// Per-connector visibility: status / last_n_calls success rate /
// last error / sample-shape sanity / median latency. Surfaces silent
// connector failures (transport-OK-but-data-broken) that an HTTP-status-
// only check would miss.
//
// Consumes GET /api/land/calibration. Backend contract pinned in
// commit b217377 (test_calibration_endpoint_returns_per_connector_status).
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATUS_TONE = {
  healthy:   'border-emerald-300 bg-emerald-50 text-emerald-800',
  degraded:  'border-amber-300 bg-amber-50 text-amber-800',
  unhealthy: 'border-red-300 bg-red-50 text-red-800',
  unknown:   'border-zinc-300 bg-zinc-50 text-zinc-600',
};

const STATUS_HELP = {
  healthy:   'last 100 success ≥ 95% AND sample-shape OK ≥ 90%',
  degraded:  'last 100 success ≥ 50% OR sample-shape < 90%',
  unhealthy: 'last 100 success < 50%',
  unknown:   'no calls recorded — connector configured but never called',
};

export default function LandCalibration() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/land/calibration`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`calibration: ${resp.status}`);
      setData(await resp.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-8 text-zinc-500">Loading calibration…</div>;
  }
  if (error) {
    return (
      <div className="p-8 text-red-700">
        Failed to load calibration: {error}
      </div>
    );
  }
  if (!data) return null;

  const { connectors = [], totals = {}, assembled_at } = data;

  return (
    <div className="min-h-screen bg-zinc-50 p-6">
      <div className="mx-auto max-w-6xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-900">
            Connector Calibration
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            Per-connector health for the Land Intelligence pipeline.
            Status surfaces silent failures (HTTP 200 + garbage data)
            that a transport-only check would miss.
          </p>
          {assembled_at && (
            <p className="mt-1 text-xs text-zinc-500">
              Assembled at {new Date(assembled_at).toLocaleString()} ·
              <button
                type="button"
                onClick={load}
                className="ml-2 underline hover:text-zinc-700"
              >refresh</button>
            </p>
          )}
        </header>

        <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          <SummaryCell label="Configured" value={totals.configured ?? 0} />
          <SummaryCell label="With calls" value={totals.with_calls ?? 0} />
          <SummaryCell
            label="Healthy" value={totals.healthy ?? 0}
            tone="text-emerald-700"
          />
          <SummaryCell
            label="Degraded" value={totals.degraded ?? 0}
            tone="text-amber-700"
          />
          <SummaryCell
            label="Unhealthy" value={totals.unhealthy ?? 0}
            tone="text-red-700"
          />
          <SummaryCell
            label="Unknown" value={totals.unknown ?? 0}
            tone="text-zinc-500"
          />
        </section>

        <section className="space-y-2">
          {connectors.map((c) => (
            <ConnectorRow key={c.connector_name} connector={c} />
          ))}
        </section>

        <section className="mt-6 rounded-xl border border-zinc-200 bg-white p-4">
          <h2 className="text-sm font-bold text-zinc-900">Status thresholds</h2>
          <ul className="mt-2 space-y-1 text-xs text-zinc-600">
            {Object.entries(STATUS_HELP).map(([s, help]) => (
              <li key={s} className="flex items-start gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[s]}`}>
                  {s}
                </span>
                <span>{help}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function SummaryCell({ label, value, tone = '' }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-bold ${tone || 'text-zinc-900'}`}>
        {value}
      </p>
    </div>
  );
}

function ConnectorRow({ connector }) {
  const {
    connector_name, status, last_n_count, success_rate,
    sample_shape_ok_rate, median_latency_ms,
    last_call_at, last_success_at, last_error_message, last_error_at,
  } = connector;

  return (
    <div className={`rounded-xl border bg-white p-4 ${
      status === 'unhealthy' ? 'border-red-300' :
      status === 'degraded'  ? 'border-amber-300' :
      'border-zinc-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-sm font-bold text-zinc-900">
            {connector_name}
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            {last_n_count > 0
              ? `${last_n_count} of last 100 calls`
              : 'no calls recorded'}
          </p>
        </div>
        <span className={`rounded-full border px-3 py-0.5 text-xs font-semibold ${STATUS_TONE[status]}`}>
          {status}
        </span>
      </div>

      <dl className="mt-3 grid gap-3 text-xs sm:grid-cols-4">
        <Metric
          label="Success rate"
          value={success_rate != null ? `${(success_rate * 100).toFixed(1)}%` : 'n/a'}
        />
        <Metric
          label="Shape-ok rate"
          value={sample_shape_ok_rate != null ? `${(sample_shape_ok_rate * 100).toFixed(1)}%` : 'n/a'}
          help="Fraction of last-100 calls whose response had the expected data shape (NOT just HTTP 200)"
        />
        <Metric
          label="Median latency"
          value={median_latency_ms != null ? `${median_latency_ms.toFixed(0)} ms` : 'n/a'}
        />
        <Metric
          label="Last success"
          value={last_success_at ? timeAgo(last_success_at) : 'never'}
        />
      </dl>

      {last_error_message && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-red-700">
            Last error{last_error_at ? ` · ${timeAgo(last_error_at)}` : ''}
          </p>
          <p className="mt-1 font-mono text-xs text-red-800">
            {last_error_message.length > 240
              ? last_error_message.slice(0, 240) + '…'
              : last_error_message}
          </p>
        </div>
      )}

      {last_call_at && (
        <p className="mt-2 text-[10px] text-zinc-400">
          last call {timeAgo(last_call_at)}
        </p>
      )}
    </div>
  );
}

function Metric({ label, value, help }) {
  return (
    <div title={help}>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        {label}
      </dt>
      <dd className="mt-0.5 font-mono text-sm font-bold text-zinc-900">{value}</dd>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.floor((now - then) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
